/**
=========================================================
* Material Dashboard 3 PRO React - v2.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "App";

// Material Dashboard 3 PRO React Context Provider
import { MaterialUIControllerProvider } from "context";

// MSAL imports
import { MsalProvider } from "@azure/msal-react";
import { initializeMsal, getMsalInstance } from "./authConfig";

// Apollo GraphQL imports
import { ApolloProvider } from "@apollo/client";
import { initializeApolloClient } from "./apolloClient";

// Fabric config service
import { fetchFabricConfig } from "./services/fabricConfig";

// Loading component
const LoadingScreen = () => (
  <div style={{
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    fontFamily: "Inter, sans-serif",
  }}>
    <div style={{
      width: "50px",
      height: "50px",
      border: "4px solid #e0e0e0",
      borderTop: "4px solid #1976d2",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    }} />
    <p style={{ marginTop: "20px", color: "#666" }}>Initializing application...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Error component
const ErrorScreen = ({ error, onRetry }) => (
  <div style={{
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    fontFamily: "Inter, sans-serif",
    padding: "20px",
  }}>
    <div style={{
      backgroundColor: "white",
      padding: "40px",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      textAlign: "center",
      maxWidth: "400px",
    }}>
      <h2 style={{ color: "#d32f2f", marginBottom: "16px" }}>Initialization Error</h2>
      <p style={{ color: "#666", marginBottom: "24px" }}>{error}</p>
      <button
        onClick={onRetry}
        style={{
          backgroundColor: "#1976d2",
          color: "white",
          border: "none",
          padding: "12px 24px",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "14px",
        }}
      >
        Retry
      </button>
    </div>
  </div>
);

// App wrapper that handles initialization
const AppWrapper = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msalInstance, setMsalInstance] = useState(null);
  const [apolloClient, setApolloClient] = useState(null);
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(false);

  const initialize = async () => {
    // Check if we're on callback route - don't show loading screen
    const isCallbackRoute = window.location.pathname === "/auth/callback";
    
    if (!isCallbackRoute) {
      setIsLoading(true);
    } else {
      setIsProcessingRedirect(true);
    }
    setError(null);

    try {
      // Step 1: Fetch fabric config from API
      console.log("Fetching fabric configuration...");
      await fetchFabricConfig();

      // Step 2: Initialize MSAL with the fetched config
      console.log("Initializing MSAL...");
      const msal = await initializeMsal();
      setMsalInstance(msal);

      // Step 3: Check for MSAL redirect promise BEFORE rendering app
      // This prevents showing login page if we're in the middle of auth flow
      console.log("Checking for MSAL redirect...");
      const redirectResponse = await msal.handleRedirectPromise();
      
      if (redirectResponse) {
        console.log("MSAL redirect detected during initialization");
        // Store tokens if available
        if (redirectResponse.accessToken) {
          localStorage.setItem("accessToken", redirectResponse.accessToken);
        }
        if (redirectResponse.account) {
          localStorage.setItem("userInfo", JSON.stringify({
            name: redirectResponse.account.name,
            email: redirectResponse.account.username,
          }));
        }
        // Redirect immediately using window.location.replace to bypass React Router
        // This prevents showing any loading screens or intermediate pages
        window.location.replace("/dashboards/my-dashboard");
        return; // Don't continue initialization - redirect will reload the page
      }

      // Step 4: Initialize Apollo Client with GraphQL endpoint from config
      console.log("Initializing Apollo Client...");
      const apollo = initializeApolloClient();
      setApolloClient(apollo);

      console.log("Application initialized successfully");
      setIsLoading(false);
      setIsProcessingRedirect(false);
    } catch (err) {
      console.error("Failed to initialize application:", err);
      setError(err.message || "Failed to initialize application");
      setIsLoading(false);
      setIsProcessingRedirect(false);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  // Don't show loading screen if processing redirect (callback route)
  if (isProcessingRedirect) {
    return null; // Return nothing while processing redirect
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorScreen error={error} onRetry={initialize} />;
  }

  if (!msalInstance || !apolloClient) {
    return <ErrorScreen error="Failed to initialize authentication" onRetry={initialize} />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <ApolloProvider client={apolloClient}>
        <BrowserRouter>
          <MaterialUIControllerProvider>
            <App />
          </MaterialUIControllerProvider>
        </BrowserRouter>
      </ApolloProvider>
    </MsalProvider>
  );
};

const container = document.getElementById("app");
const root = createRoot(container);

root.render(<AppWrapper />);
